import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftRight, ArrowRight, AudioLines, ChevronDown, Coffee, Gamepad2, Headset, Heart, LayoutGrid, Maximize2, Mic, MicOff, Minimize2, Monitor,
  QrCode, RefreshCw, RotateCcw, Settings, Swords, Timer, Trophy, Video, Volume2, VolumeX, Zap,
} from 'lucide-react';
import { DEFAULT_OBS_URL, IOS_DEVICE, isLoopbackHost, obsControl } from '../lib/obsControl.js';
import { obsDirector } from '../lib/obsDirector.js';
import { obsRemote, pairedUrl, parseObsws, showHost } from '../lib/obsRemote.js';
import { CUT_AFTER_MS, obsStage } from '../lib/obsStage.js';
import QrScanner, { cameraAvailable } from './QrScanner.jsx';

// OBS Control tab on the PackageProduction page — the UI over the
// obsControl singleton (src/lib/obsControl.js; spec in
// docs/OBS_SCENE_SWITCHING_BRIEF.md). The connection to OBS is this
// browser's own, over ws://127.0.0.1 on the machine running OBS: our server
// never reaches OBS. A console on another device (the iPad) has no such
// connection, so its tiles cut THROUGH the PC's tab: src/lib/obsRemote.js,
// by way of the server's obs_remote row, which carries the cut and the PC
// tab's state. The same row carries OBS's Connect Info scanned from its QR
// code (QrScanner.jsx), so the PC's tab is paired without typing.
//
// One scene tile per granted overlay: the scene KEY is the overlay slug,
// the default scene NAME is the overlay display name — the exact names
// "Export to OBS" writes into the scene collection, so export → import →
// control works with zero typing. The scene-name map (in the Connection
// panel) covers operators who renamed scenes or built their collection by
// hand. Number keys 1–9 cut to the tiles in order while the tab is open.
//
// Under the tiles (src/lib/obsStage.js, built for the KEC packages and on
// for any package with those screens): cuts when Starting Soon's or Be Right
// Back's countdown ends, the Commentator cameras following the one-or-two
// camera switch (which the layout buttons here also set), the stream's sound
// (OBS's mixer: its global audio devices, and any other audio source on air;
// mute, volume, level) and In-Match's side swap. Their settings are this machine's, like the connection. The layout is
// the "deck" the club picked (2026-10-19): tiles three across, then the
// automation beside the mixer; two across and stacked on a phone.
//
// With `deck` (src/pages/Deck.jsx, /<package>/deck) the card is the whole
// page: the tiles fill the screen, the panels under them follow (the OBS
// PC's, through the relay, on a remote), so an iPad is a stream deck; the
// header links there and back (the console opens on its OBS tab).

const STATUS_LABEL = {
  connected: 'Connected',
  connecting: 'Connecting…',
  disconnected: 'Not connected',
  'auth-failed': 'Wrong password',
  blocked: 'Blocked by Chrome',
  disabled: 'Off',
};

const STREAM_ORDER = {
  'starting-soon': 1,
  'up-next': 2,
  commentator: 3,
  'camera-feed': 4,
  'in-match': 5,
  'winner-screen': 6,
  // VALORANT's whole bracket, aired between matches: beside In-Match.
  bracket: 7,
  'be-right-back': 8,
  'top-8': 9,
  'top-8-bracket': 10,
  'thank-you': 11,
};

const SCENE_ICONS = {
  'starting-soon': Timer,
  'up-next': Swords,
  commentator: Headset,
  'in-match': Gamepad2,
  'be-right-back': Coffee,
  'thank-you': Heart,
  'winner-screen': Trophy,
  'top-8': Trophy,
  'top-8-bracket': LayoutGrid,
  bracket: LayoutGrid,
  'camera-feed': Video,
};

const hostOf = (url) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const logTime = (ts) =>
  new Date(ts).toLocaleTimeString([], { hour12: false });

const mmss = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// Re-renders its caller once a second, for the countdowns on the tiles and
// the "cuts in" times; kept to the small components that show them.
function useNow(on = true) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

// A switch drawn as the deck's toggle; a real button with role="switch".
function Toggle({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={`obs-toggle${checked ? ' is-on' : ''}`}
      onClick={() => onChange(!checked)}
    />
  );
}

// The faders run from MIN_DB (silence: sent as OBS's −100 dB floor) to 0 dB,
// as OBS's own mixer does; the meters show −60 to 0 dB, ticked where OBS
// changes colour.
const MIN_DB = -50;
const METER_FLOOR_DB = -60;
const METER_TICKS = [-60, -40, -20, -9, 0];
const fmtDb = (db) => (db <= MIN_DB ? '−∞ dB' : `${db.toFixed(1).replace('-', '−')} dB`);
const meterPct = (db) =>
  Number.isFinite(db) ? Math.max(0, Math.min(100, ((db - METER_FLOOR_DB) / -METER_FLOOR_DB) * 100)) : 0;

// One source in OBS's mixer: mute, level and peak, volume. A global device
// plays on every scene; any other is tagged as the scene's own, shown while
// that scene is on air, as OBS's mixer does. The global mic, once the stage
// has picked the package's device for it (obsControl.mic), is tagged with
// the mic's model, or in red when OBS has it on another device (the device's
// name in the tag's title). The meter has its own subscription so ~15 level
// updates a second repaint only it.
function AudioStrip({
  input, mic, disabled,
  onMute = (muted) => obsControl.setInputMuted(input.name, muted),
  onVolume = (db) => obsControl.setInputVolumeDb(input.name, db),
}) {
  const meters = useSyncExternalStore(obsControl.subscribeMeters, obsControl.getMeters);
  // While the fader is held, its own position, kept once it lets go until
  // the input's volume reaches it: OBS's InputVolumeChanged brings the
  // value back at once on the PC, a poll later through the relay; 3 s at
  // most, so a refused volume lets go too.
  const [draft, setDraft] = useState(null);
  const [held, setHeld] = useState(false);
  const stored = Number(input.volumeDb);
  useEffect(() => {
    if (draft === null || held) return undefined;
    if (Number.isFinite(stored) && Math.abs(Math.max(MIN_DB, stored) - draft) < 0.25) {
      setDraft(null);
      return undefined;
    }
    const t = setTimeout(() => setDraft(null), 3000);
    return () => clearTimeout(t);
  }, [draft, held, stored]);
  const db = draft ?? Math.max(MIN_DB, Math.min(0, Number.isFinite(stored) ? stored : MIN_DB));
  const Icon = input.role === 'mic'
    ? (input.muted ? MicOff : Mic)
    : input.muted ? VolumeX : input.role === 'source' ? AudioLines : Volume2;
  const meter = input.muted ? null : meters[input.name];
  const fill = ((db - MIN_DB) / -MIN_DB) * 100;
  return (
    <div className={`obs-strip${input.muted ? ' is-muted' : ''}`}>
      <button
        type="button"
        className="obs-mute"
        disabled={disabled}
        aria-pressed={input.muted}
        title={input.muted ? `Unmute ${input.name}` : `Mute ${input.name}`}
        onClick={() => onMute(!input.muted)}
      >
        <Icon size={17} aria-hidden="true" />
      </button>
      <div className="obs-strip__body">
        <div className="obs-strip__top">
          <span className="obs-strip__name">
            {input.name}
            {input.role === 'source' && <small className="obs-strip__tag">This scene</small>}
            {mic && (
              <small
                className={`obs-strip__tag${mic.matched ? '' : ' is-wrong'}`}
                title={mic.matched
                  ? `OBS's mic: ${mic.device}`
                  : `OBS's mic is "${mic.device}" — plug the ${mic.model} in and reconnect, or pick it in OBS > Settings > Audio`}
              >
                {mic.matched ? mic.model : `Not the ${mic.model}`}
              </small>
            )}
          </span>
          <span className="obs-strip__db">{input.muted ? 'MUTED' : fmtDb(db)}</span>
        </div>
        <div className="obs-meter" aria-hidden="true">
          <span style={{ clipPath: `inset(0 ${100 - meterPct(meter?.level)}% 0 0)` }} />
          {meter && Number.isFinite(meter.peak) && <b style={{ left: `${meterPct(meter.peak)}%` }} />}
        </div>
        <div className="obs-ticks" aria-hidden="true">
          {METER_TICKS.map((t) => (
            <span key={t} style={{ left: `${meterPct(t)}%` }}>{t === 0 ? '0' : `−${-t}`}</span>
          ))}
        </div>
        <input
          type="range"
          className="obs-fader"
          min={MIN_DB}
          max={0}
          step={0.5}
          value={db}
          disabled={disabled}
          style={{ '--fill': `${fill}%` }}
          aria-label={`${input.name} volume`}
          onChange={(e) => {
            const v = Number(e.target.value);
            setDraft(v);
            onVolume(v <= MIN_DB ? -100 : v);
          }}
          onPointerDown={() => setHeld(true)}
          onPointerUp={() => setHeld(false)}
          onPointerCancel={() => setHeld(false)}
          onBlur={() => setHeld(false)}
        />
      </div>
    </div>
  );
}

// Chrome refused the connection (BROWSER_ACCESS in obsControl.js). The fix
// is in Chrome's site settings, which a page can't link to (chrome:// pages
// are off limits), so the panel offers the address to copy.
function BlockedHelp({ label }) {
  const settingsUrl = `chrome://settings/content/siteDetails?site=${encodeURIComponent(window.location.origin)}`;
  const [copied, setCopied] = useState(false);
  const fieldRef = useRef(null);
  async function copy() {
    let ok = false;
    try {
      await navigator.clipboard.writeText(settingsUrl);
      ok = true;
    } catch {
      // A refused Clipboard API: copy the selected field the old way (as
      // the console's CopyableUrl does).
      fieldRef.current?.select();
      try {
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }
    }
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2000);
    // Neither worked: the address is left selected for Ctrl+C.
    else fieldRef.current?.select();
  }
  return (
    <div className="obs-blocked mt-3" role="alert">
      <p className="mb-2">
        <strong>Chrome is blocking this site from reaching OBS.</strong> The connection stops inside the browser, so
        OBS never sees it and nothing in OBS fixes it.
      </p>
      <ol className="mb-2">
        <li>
          Copy this address, paste it into Chrome&rsquo;s address bar and press Enter:
          <div className="input-group input-group-sm mt-1">
            <input
              ref={fieldRef}
              className="form-control"
              readOnly
              value={settingsUrl}
              onFocus={(e) => e.target.select()}
              aria-label="Chrome's settings for this site"
            />
            <button type="button" className="btn btn-outline-primary" onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </li>
        <li>
          Set <strong>{label}</strong> to <strong>Allow</strong>
          {label === 'Apps on device' && <> (not &ldquo;Local network&rdquo;, which is about other computers)</>}.
        </li>
        <li>Come back to this tab: it connects by itself (or press Apply &amp; connect).</li>
      </ol>
      <p className="obs-note mb-0">Chrome keeps this per computer: allow it on every PC that runs this console.</p>
    </div>
  );
}

// What a tile says under its name: the screen's state where there is one.
function TileMeta({ overlay, stage, mapped }) {
  const ticking = overlay.slug === 'starting-soon' || overlay.slug === 'be-right-back';
  const now = useNow(ticking);
  let text = '';
  if (ticking) {
    const end = stage.countdowns[overlay.slug === 'starting-soon' ? 'starting' : 'brb'];
    text = end && end > now ? `Countdown ${mmss(end - now)}` : 'No countdown';
  } else if (overlay.slug === 'commentator' && stage.camerasAvailable) {
    text = stage.booth
      ? (stage.layout === 'one' ? 'Camera only' : 'Up Next + Bracket')
      : (stage.layout === 'one' ? 'One camera' : 'Two cameras');
  } else if (overlay.slug === 'in-match' && stage.swapped === true) {
    text = 'Sides swapped';
  } else if (overlay.slug === 'in-match') {
    text = 'Over the game';
  } else if (overlay.slug === 'up-next') {
    text = 'The next match';
  } else if (overlay.slug === 'bracket') {
    text = 'The whole bracket';
  } else if (overlay.slug === 'thank-you') {
    text = 'Ends the stream';
  }
  if (mapped && mapped !== overlay.name) text = text ? `${text} · "${mapped}"` : `"${mapped}"`;
  return text ? <span className="obs-tile__meta">{text}</span> : null;
}

// "Starting Soon 0:00 → [scene]" with its switch, and when the cut is due.
function CutRow({ which, label, from, cut, nextCut, countdownEnd, overlays, onChange = (patch) => obsStage.setCut(which, patch) }) {
  const now = useNow(cut.on);
  const when = !cut.on
    ? 'off'
    : nextCut
      ? `cuts in ${mmss(nextCut.at - now)}`
      : countdownEnd && countdownEnd > now - CUT_AFTER_MS
        ? 'cutting…'
        : 'no countdown';
  return (
    <div className={`obs-auto${cut.on ? ' is-on' : ''}`}>
      <span className="obs-auto__group">
        <Toggle checked={cut.on} label={`Cut from ${label} when its countdown ends`} onChange={(on) => onChange({ on })} />
        <span className="obs-chip"><Timer size={14} aria-hidden="true" />{label} 0:00</span>
      </span>
      <span className="obs-auto__group">
        <ArrowRight size={16} className="obs-arrow" aria-hidden="true" />
        <span className="obs-chip obs-chip--select">
          <select value={cut.to ?? ''} aria-label={`Scene after ${label}`} onChange={(e) => onChange({ to: e.target.value })}>
            {overlays.filter((o) => o.slug !== from).map((o) => (
              <option value={o.slug} key={o.slug}>{o.name}</option>
            ))}
          </select>
          <ChevronDown size={14} aria-hidden="true" />
        </span>
      </span>
      <span className={`obs-auto__when${nextCut && cut.on ? ' is-due' : ''}`}>{when}</span>
    </div>
  );
}

const TwoWindows = () => (
  <svg width="64" height="32" viewBox="0 0 64 32" aria-hidden="true">
    <rect x="1" y="1" width="62" height="30" rx="3" />
    <rect className="w" x="7" y="7" width="23" height="13" rx="1.5" />
    <rect className="w" x="34" y="7" width="23" height="13" rx="1.5" />
  </svg>
);
const OneWindow = () => (
  <svg width="64" height="32" viewBox="0 0 64 32" aria-hidden="true">
    <rect x="1" y="1" width="62" height="30" rx="3" />
    <rect className="w" x="16" y="4" width="32" height="18" rx="1.5" />
  </svg>
);

export default function ObsControlCard({ packageSlug, overlays, onSharedChange, deck = false, packageName = null }) {
  const obs = useSyncExternalStore(obsControl.subscribe, obsControl.getSnapshot);
  const director = useSyncExternalStore(obsDirector.subscribe, obsDirector.getSnapshot);
  const stage = useSyncExternalStore(obsStage.subscribe, obsStage.getSnapshot);
  const remote = useSyncExternalStore(obsRemote.subscribe, obsRemote.getSnapshot);
  // Tiles in a stream's order, so 1 is the first screen on air and the
  // number keys follow the show; screens without a place keep theirs, after.
  const list = useMemo(
    () => (overlays ?? [])
      .map((o, i) => ({ o, i }))
      .sort((a, b) => (STREAM_ORDER[a.o.slug] ?? 99) - (STREAM_ORDER[b.o.slug] ?? 99) || a.i - b.i)
      .map(({ o }) => o),
    [overlays],
  );
  // The auto winner screen is for packages with a Winner Screen (Tekken 8);
  // elsewhere its switch would only ever say it is unavailable.
  const hasWinnerScreen = list.some((o) => o.slug === 'winner-screen');

  // slug → display name in grant (position) order; doubles as the default
  // scene map and the tile list.
  const defaults = useMemo(
    () => Object.fromEntries(list.map((o) => [o.slug, o.name])),
    [list],
  );

  useEffect(() => {
    obsControl.activateSceneMap(packageSlug, defaults);
    obsControl.connect();
  }, [packageSlug, defaults]);

  // While the card is open this tab looks at the OBS PC's tab as a remote
  // (a tab with control off; one with it on connects itself).
  useEffect(() => {
    obsRemote.watch(true);
    return () => obsRemote.watch(false);
  }, []);

  // Connection inputs are local until "Apply & connect" — live-writing the
  // URL character by character would tear down/redial the socket per key.
  const [form, setForm] = useState(() => ({
    url: obs.connection.url,
    password: obs.connection.password,
  }));
  // Studio Mode ON defaults to the spec's preview-then-take flow; the
  // switch lets an operator force direct cuts without leaving Studio Mode.
  const [stageOnPreview, setStageOnPreview] = useState(true);

  const enabled = obs.connection.enabled;
  const connected = obs.status === 'connected';
  // The relay (the console tab on the OBS PC, src/lib/obsRemote.js) as the
  // server last described it: alive and connected, this tab's tiles cut
  // through it, whatever the toggle here (a phone with control on by
  // mistake still cuts through the PC; a note under the tiles says to switch
  // it off). Never this tab's own heartbeat (a PC whose OBS closed).
  const relay = remote.relay;
  const relayAlive = !connected && relay?.alive === true && relay.mine !== true;
  const viaRelay = relayAlive && relay.status === 'connected';
  const canCut = connected || viaRelay;
  const [scanning, setScanning] = useState(false);
  const mixedContentRisk =
    window.location.protocol === 'https:' && !isLoopbackHost(form.url);
  // The connection settings stay folded once control is on (the status pill
  // says how it's going); they open while it's off, when OBS refuses the
  // password, and when Chrome blocks the connection or is asking about it.
  // On the deck page it stays folded: a remote needs it only to scan.
  const [showConnection, setShowConnection] = useState(() => !deck && !enabled);
  const chromeAsking = obs.status === 'connecting' && obs.browserAccess?.state === 'prompt';
  useEffect(() => {
    if (obs.status === 'auth-failed' || obs.status === 'blocked' || chromeAsking) setShowConnection(true);
  }, [obs.status, chromeAsking]);

  // OBS's Connect Info pasted whole (obsws://host:port/password) fills both
  // fields, at the loopback address a tab can use.
  function applyConnection(nextEnabled) {
    const pasted = parseObsws(form.url);
    const next = pasted ? { url: pairedUrl(pasted.port), password: pasted.password } : form;
    if (pasted) setForm(next);
    obsControl.saveConnection({ ...next, enabled: nextEnabled });
  }

  // The QR code read: handed to the OBS PC's tab through the server, and
  // applied here too when this tab is the one with OBS control on.
  function onScanned(text) {
    setScanning(false);
    const info = parseObsws(text);
    if (!info) return;
    if (enabled) setForm({ url: pairedUrl(info.port), password: info.password });
    obsRemote.pair(info);
  }

  function switchTo(sceneKey) {
    if (!connected && viaRelay) obsRemote.cut(sceneKey);
    else if (obs.studioMode && stageOnPreview) obsControl.previewAndTake(sceneKey);
    else obsControl.setScene(sceneKey);
  }

  // Number keys 1–9 cut to the tiles in order, unless the operator is typing.
  useEffect(() => {
    function onKey(e) {
      if (e.repeat || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const el = e.target;
      if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return;
      const n = Number(e.key);
      if (!Number.isInteger(n) || n < 1 || n > Math.min(9, list.length)) return;
      if (obsControl.isConnected || obsRemote.viaRelay) switchTo(list[n - 1].slug);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // The panels under the tiles show this tab's OBS when it is connected,
  // else the OBS PC's as its tab reports it (src/lib/obsRemote.js), with
  // their controls going there; with neither, this machine's own settings,
  // and the notes say to connect.
  const rs = viaRelay ? relay.stage : null;
  const nextCutVia = (which) => {
    const end = stage.countdowns[which];
    return rs?.cuts?.[which]?.on && end && end + CUT_AFTER_MS > Date.now() ? { at: end + CUT_AFTER_MS } : null;
  };
  const panel = rs
    ? {
      audio: rs.audio ?? [],
      mic: rs.mic ?? null,
      swapped: rs.swapped ?? null,
      cuts: rs.cuts ?? stage.settings.cuts,
      nextCut: { starting: nextCutVia('starting'), brb: nextCutVia('brb') },
      camerasFollow: rs.camerasFollow === true,
      layout: rs.layout ?? null,
      applied: rs.applied ?? null,
      canLayout: rs.console === true,
      mute: (name, muted) => obsRemote.send({ action: 'mute', input: name, muted }),
      volume: (name, db) => obsRemote.send({ action: 'volume', input: name, db }),
      swap: () => obsRemote.send({ action: 'swap', swapped: rs.swapped !== true }),
      setCut: (which, cutPatch) => obsRemote.send({ action: 'cutSetting', which, ...cutPatch }),
      setCamerasFollow: (on) => obsRemote.send({ action: 'camerasFollow', on }),
      setLayout: (shared) => obsRemote.send({ action: 'layout', shared }),
    }
    : {
      audio: obs.audio,
      mic: obs.mic,
      swapped: stage.swapped,
      cuts: stage.settings.cuts,
      nextCut: stage.nextCut,
      camerasFollow: stage.settings.camerasFollow,
      layout: stage.layout,
      applied: stage.applied,
      canLayout: !!onSharedChange,
      mute: (name, muted) => obsControl.setInputMuted(name, muted),
      volume: (name, db) => obsControl.setInputVolumeDb(name, db),
      swap: () => obsStage.swapSides(),
      setCut: (which, cutPatch) => obsStage.setCut(which, cutPatch),
      setCamerasFollow: (on) => obsStage.setCamerasFollow(on),
      setLayout: (shared) => onSharedChange?.(shared),
    };
  const unit = packageSlug === 'marvel-rivals' ? 'Team' : 'Player';
  const sides = panel.swapped ? [`${unit} 2`, `${unit} 1`] : [`${unit} 1`, `${unit} 2`];
  const hasCuts = stage.cutsAvailable.starting || stage.cutsAvailable.brb;
  const cameraStatus = !panel.camerasFollow
    ? 'Off: OBS’s cameras stay as you set them.'
    : !canCut
      ? 'OBS follows once it’s connected.'
      : panel.applied !== panel.layout
        ? 'Updating OBS…'
        : stage.booth
          ? (panel.layout === 'one' ? 'OBS shows the camera alone.' : 'OBS shows Up Next and the bracket in the windows.')
          : panel.layout === 'one'
            ? 'OBS shows One Camera for Both.'
            : 'OBS shows Left and Right Commentator Camera.';

  return (
    <div className={`obs-deck${deck ? ' is-fullscreen' : ''}`}>
      {/* ── header: status + the connection settings, folded ── */}
      <div className="obs-head">
        <img src="/images/OBS_Studio_logo.png" alt="" aria-hidden="true" className="obs-head__logo" />
        <span className="obs-head__title">{deck && packageName ? `${packageName} deck` : 'OBS Control'}</span>
        <span
          className={`obs-status is-${viaRelay ? 'remote' : relayAlive ? 'remote-off' : obs.status}`}
          role="status"
        >
          <i aria-hidden="true" />
          {viaRelay
            ? 'Through the OBS PC'
            : relayAlive
              ? `OBS PC: ${STATUS_LABEL[relay.status] ?? relay.status}`
              : STATUS_LABEL[obs.status] ?? obs.status}
          {connected && (
            <small>· {isLoopbackHost(obs.connection.url) ? 'OBS on this PC' : hostOf(obs.connection.url)}</small>
          )}
          {viaRelay && <small>· cuts run on the streaming PC</small>}
        </span>
        <span className="obs-head__spacer" />
        <Link
          className="obs-ghost"
          to={deck ? `/${packageSlug}?tab=obs` : `/${packageSlug}/deck`}
          title={deck ? 'Back to the console' : 'The deck alone, full screen: an iPad as a stream deck'}
        >
          {deck ? <Minimize2 size={14} aria-hidden="true" /> : <Maximize2 size={14} aria-hidden="true" />}
          <span className="obs-ghost__text">{deck ? 'Console' : 'Deck'}</span>
        </Link>
        <button
          type="button"
          className={`obs-ghost${showConnection ? ' is-open' : ''}`}
          aria-expanded={showConnection}
          onClick={() => setShowConnection((v) => !v)}
        >
          <Settings size={14} aria-hidden="true" />
          <span className="obs-ghost__text">Connection</span>
          <ChevronDown size={14} aria-hidden="true" className="obs-ghost__chev" />
        </button>
      </div>

      {showConnection && (
        <div className="obs-conn">
          <div className="obs-conn__row">
            <label className="obs-switch">
              <Toggle checked={enabled} label="Enable OBS control" onChange={(on) => applyConnection(on)} />
              <span>Enable OBS control</span>
            </label>
            <div className="obs-conn__field">
              <label className="form-label small mb-1" htmlFor="obs-control-url">WebSocket URL</label>
              <input
                id="obs-control-url"
                className="form-control form-control-sm"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder={DEFAULT_OBS_URL}
                disabled={!enabled}
              />
            </div>
            <div className="obs-conn__field">
              <label className="form-label small mb-1" htmlFor="obs-control-password">Password</label>
              <input
                id="obs-control-password"
                type="password"
                className="form-control form-control-sm"
                autoComplete="off"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="from WebSocket Server Settings"
                disabled={!enabled}
              />
            </div>
            <div className="obs-conn__actions">
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => applyConnection(true)} disabled={!enabled}>
                Apply &amp; connect
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => obsControl.disconnect()}
                disabled={!connected && obs.status !== 'connecting'}
              >
                Disconnect
              </button>
              {cameraAvailable() && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                  onClick={() => setScanning(true)}
                  title="Read the QR code in OBS's WebSocket Connect Info with this device's camera: the port and password go to the console on the OBS PC"
                >
                  <QrCode size={13} aria-hidden="true" /> Scan OBS&rsquo;s QR
                </button>
              )}
            </div>
          </div>
          {remote.pairing && (
            <p className={`obs-note mt-2 mb-0${remote.pairing.taken ? ' text-success' : ''}`}>
              {remote.pairing.pending
                ? `Scanned OBS at ${showHost(remote.pairing.host)}:${remote.pairing.port}. Waiting for the console on the OBS PC to take it — keep one open there with OBS control on.`
                : remote.pairing.taken
                  ? `The OBS PC took the scan: it connects to OBS at ${showHost(remote.pairing.host)}:${remote.pairing.port}.`
                  : `The scan of ${showHost(remote.pairing.host)}:${remote.pairing.port} was not taken within 10 minutes. Open this console on the OBS PC with OBS control on, then scan again.`}
            </p>
          )}
          {IOS_DEVICE && (
            <p className="obs-note mt-2 mb-0">
              An iPhone or iPad can&rsquo;t connect to OBS itself: this console cuts through the console on the OBS PC,
              and Scan OBS&rsquo;s QR pairs that PC. OBS control can stay off here.
            </p>
          )}
          {!enabled && (
            <p className="obs-note mt-2 mb-0">
              Control is off: this tab does not connect to OBS. The tiles still cut through the console on the OBS PC
              while one is open there with OBS control on. Overlays keep working either way.
            </p>
          )}
          {enabled && !viaRelay && (
            <p className="obs-note mt-2 mb-0">
              This account&rsquo;s consoles on other devices (an iPad) cut through this tab while it stays open on the
              OBS PC{remote.relayError ? ` — it can't report to the server right now: ${remote.relayError}` : ''}.
              Chrome can put a background tab to sleep: in Chrome&rsquo;s Performance settings add this site to
              &ldquo;Always keep these sites active&rdquo;.
            </p>
          )}
          {enabled && obs.status === 'blocked' && <BlockedHelp label={obs.browserAccess?.label ?? 'Apps on device'} />}
          {enabled && !connected && obs.status !== 'blocked' && obs.browserAccess?.state === 'prompt' && (
            <p className="obs-note mt-2 mb-0">
              Chrome asks before a website may reach apps on this PC: if it asks about this site, choose{' '}
              <strong>Allow</strong>.
            </p>
          )}
          {enabled && mixedContentRisk && (
            <p className="small mt-2 mb-0" style={{ color: 'var(--warning)' }}>
              Browsers only allow <code>ws://localhost</code> / <code>ws://127.0.0.1</code> from an https page — a LAN
              address like this will be blocked. Open this console on the machine running OBS.
            </p>
          )}
          <details className="obs-map mt-3">
            <summary className="small">Scene names</summary>
            <p className="obs-note mt-2 mb-2">
              The tiles target these OBS scene names. The defaults match the &ldquo;OBS Scenes&rdquo; export — only edit if
              you renamed scenes in OBS.
            </p>
            <div className="row g-2">
              {list.map((o) => (
                <div className="col-md-6 col-lg-4" key={o.slug}>
                  <label className="form-label small mb-1" htmlFor={`obs-map-${o.slug}`}>{o.name}</label>
                  <input
                    id={`obs-map-${o.slug}`}
                    className="form-control form-control-sm"
                    list="obs-live-scenes"
                    value={obs.sceneMap[o.slug] ?? ''}
                    onChange={(e) => obsControl.saveSceneMap({ ...obs.sceneMap, [o.slug]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            <datalist id="obs-live-scenes">
              {obs.scenes.map((name) => <option value={name} key={name} />)}
            </datalist>
            <div className="d-flex gap-2 mt-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                onClick={() => obsControl.refreshScenes()}
                disabled={!connected}
                title="Re-read the scene list from OBS (fills the suggestions above)"
              >
                <RefreshCw size={13} aria-hidden="true" /> Sync scenes from OBS
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                onClick={() => obsControl.saveSceneMap(defaults)}
                title="Restore the exported scene-collection names"
              >
                <RotateCcw size={13} aria-hidden="true" /> Reset to defaults
              </button>
            </div>
          </details>
        </div>
      )}

      <div className="obs-body">
        {/* ── scenes ── */}
        <div className="obs-label">
          Scenes
          <small>
            {viaRelay
              ? `click to cut through the OBS PC · keys 1–${Math.min(9, list.length)}`
              : !connected
                ? 'connect to OBS to cut'
                : obs.studioMode && stageOnPreview
                  ? 'Studio Mode: click to stage, then it takes'
                  : `click to cut · keys 1–${Math.min(9, list.length)}`}
          </small>
        </div>
        <div className="obs-tiles">
          {list.map((o, i) => {
            const mapped = obs.sceneMap[o.slug];
            const isLive = connected
              ? !!mapped && mapped === obs.currentProgramScene
              : viaRelay && (relay.key ? relay.key === o.slug : !!mapped && mapped === relay.scene);
            const isPreview = connected && obs.studioMode && !!mapped && mapped === obs.currentPreviewScene;
            const isPending = viaRelay && remote.pending?.key === o.slug;
            const Icon = SCENE_ICONS[o.slug] ?? Monitor;
            return (
              <button
                key={o.slug}
                type="button"
                className={`obs-tile${isLive ? ' is-live' : ''}${isPreview ? ' is-preview' : ''}${isPending ? ' is-pending' : ''}`}
                disabled={!canCut}
                onClick={() => switchTo(o.slug)}
                title={mapped ? (viaRelay ? `Cut to "${mapped}" on the OBS PC` : `Switch to "${mapped}"`) : 'No scene name mapped'}
              >
                <span className="obs-tile__icon"><Icon size={20} aria-hidden="true" /></span>
                <span className="obs-tile__text">
                  <span className="obs-tile__num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="obs-tile__name">{o.name}</span>
                  <TileMeta overlay={o} stage={stage} mapped={mapped} />
                </span>
                {isLive && <span className="obs-tag is-live">On air</span>}
                {!isLive && isPreview && <span className="obs-tag is-preview">Preview</span>}
                {!isLive && !isPreview && isPending && <span className="obs-tag is-pending">Sending…</span>}
                {i < 9 && !isLive && !isPreview && !isPending && <span className="obs-key" aria-hidden="true">{i + 1}</span>}
              </button>
            );
          })}
        </div>
        {!canCut && !relayAlive && (
          <p className="obs-note mt-2 mb-0">
            On another device? Keep this console open on the OBS PC with OBS control on, and this one cuts through it.
          </p>
        )}
        {relayAlive && !viaRelay && (
          <p className="obs-note mt-2 mb-0">
            The console on the OBS PC is open but not connected to OBS ({STATUS_LABEL[relay.status] ?? relay.status}):
            fix it there.
          </p>
        )}
        {enabled && viaRelay && (
          <p className="small mt-2 mb-0" style={{ color: 'var(--warning)' }}>
            OBS control is on here, but this device is not connected to OBS: the tiles cut through the console on the
            OBS PC. Switch OBS control off here (under Connection) unless this is the OBS PC.
          </p>
        )}
        {obs.studioMode && (
          <label className="obs-switch obs-switch--small mt-2">
            <Toggle checked={stageOnPreview} label="Stage on preview, then take" onChange={setStageOnPreview} />
            <span>Studio Mode is on — stage on preview, then Take (off for direct cuts)</span>
          </label>
        )}

        <div className="obs-cols">
          <div className="obs-col">
            {(hasCuts || hasWinnerScreen) && (
              <section className="obs-panel">
                <h6 className="obs-label">
                  <Zap size={13} aria-hidden="true" /> Automation
                  {viaRelay && <small>on the OBS PC</small>}
                </h6>
                {stage.cutsAvailable.starting && (
                  <CutRow
                    which="starting"
                    label="Starting Soon"
                    from="starting-soon"
                    cut={panel.cuts.starting}
                    nextCut={panel.nextCut.starting}
                    countdownEnd={stage.countdowns.starting}
                    overlays={list}
                    onChange={(cutPatch) => panel.setCut('starting', cutPatch)}
                  />
                )}
                {stage.cutsAvailable.brb && (
                  <CutRow
                    which="brb"
                    label="Be Right Back"
                    from="be-right-back"
                    cut={panel.cuts.brb}
                    nextCut={panel.nextCut.brb}
                    countdownEnd={stage.countdowns.brb}
                    overlays={list}
                    onChange={(cutPatch) => panel.setCut('brb', cutPatch)}
                  />
                )}
                {hasCuts && (
                  <p className="obs-note mt-2 mb-0">
                    Cuts {CUT_AFTER_MS / 1000} s after 0:00, once the timer has left the screen, and only if OBS is still
                    on that screen.
                  </p>
                )}
                {hasWinnerScreen && !viaRelay && (
                  <div className={hasCuts ? 'mt-3' : ''}>
                    <label className="obs-switch">
                      <Toggle
                        checked={director.armed}
                        disabled={!enabled || !director.available}
                        label="Auto winner screen"
                        onChange={(on) => obsDirector.setArmed(on)}
                      />
                      <span>Auto winner screen</span>
                    </label>
                    <p className="obs-note mt-1 mb-0">
                      {director.armed
                        ? director.watching
                          ? director.lastAction || 'Watching the stream'
                          : 'Armed — starts watching as soon as OBS is connected'
                        : director.available
                          ? 'When a match ends: stamps the winner into the Winner card, cuts In-Match → Winner Screen, and cuts back when the next set goes live. Armed on this machine only.'
                          : `Unavailable — ${director.reason}.`}
                    </p>
                  </div>
                )}
              </section>
            )}

            {stage.camerasAvailable && (
              <section className="obs-panel">
                <h6 className="obs-label">
                  {stage.booth ? 'Commentator windows' : 'Commentator cameras'}
                  {viaRelay && <small>on the OBS PC</small>}
                </h6>
                <div className="obs-layouts">
                  <button
                    type="button"
                    className={`obs-layout${panel.layout !== 'one' ? ' is-on' : ''}`}
                    aria-pressed={panel.layout !== 'one'}
                    disabled={!panel.canLayout || (!stage.booth && stage.namedCount === 1)}
                    title={stage.booth
                      ? 'Up Next and the bracket in the two windows'
                      : stage.namedCount === 1 ? 'Only one commentator has a name' : 'A window per commentator'}
                    onClick={() => panel.setLayout(false)}
                  >
                    <TwoWindows />
                    {stage.booth ? 'Up Next + Bracket' : 'Two cameras'}
                  </button>
                  <button
                    type="button"
                    className={`obs-layout${panel.layout === 'one' ? ' is-on' : ''}`}
                    aria-pressed={panel.layout === 'one'}
                    disabled={!panel.canLayout}
                    title={stage.booth ? 'The camera alone, larger' : 'One camera for both commentators'}
                    onClick={() => panel.setLayout(true)}
                  >
                    <OneWindow />
                    {stage.booth ? 'Camera only' : 'One for both'}
                  </button>
                </div>
                {viaRelay && !panel.canLayout && (
                  <p className="obs-note mt-2 mb-0">
                    To switch the layout from here, open the console (not the deck alone) on the OBS PC.
                  </p>
                )}
                <label className="obs-switch mt-3">
                  <Toggle
                    checked={panel.camerasFollow}
                    label={stage.booth ? "OBS's camera, Up Next and Bracket follow the layout" : "OBS's cameras follow the layout"}
                    onChange={(on) => panel.setCamerasFollow(on)}
                  />
                  <span>{stage.booth ? 'OBS\u2019s camera, Up Next and Bracket follow the layout' : 'OBS\u2019s cameras follow the layout'}</span>
                </label>
                <p className="obs-note mt-1 mb-0">{cameraStatus}</p>
              </section>
            )}
          </div>

          <div className="obs-col">
            <section className="obs-panel">
              <h6 className="obs-label">Mixer{viaRelay && <small>on the OBS PC</small>}</h6>
              {!canCut ? (
                <p className="obs-note mb-0">Connect to OBS to see the mic, the PC&rsquo;s sound and the scene&rsquo;s own.</p>
              ) : panel.audio.length === 0 ? (
                <p className="obs-note mb-0">
                  OBS has no sound on air: no global audio devices (OBS &gt; Settings &gt; Audio) and no audio source in
                  this scene.
                </p>
              ) : (
                panel.audio.map((a) => (
                  <AudioStrip
                    key={a.name}
                    input={a}
                    mic={panel.mic?.input === a.name ? panel.mic : null}
                    disabled={false}
                    onMute={(muted) => panel.mute(a.name, muted)}
                    onVolume={(db) => panel.volume(a.name, db)}
                  />
                ))
              )}
              {viaRelay && panel.audio.length > 0 && (
                <p className="obs-note mt-2 mb-0">The level meters show on the OBS PC only.</p>
              )}
            </section>

            {stage.swapAvailable && (
              <section className="obs-panel">
                <h6 className="obs-label">
                  In-Match sides
                  <small>{panel.swapped ? 'swapped' : 'as start.gg orders them'}</small>
                </h6>
                <div className="obs-sides">
                  <div className="obs-plate"><small>Left</small><b>{sides[0]}</b></div>
                  <button
                    type="button"
                    className="obs-swap"
                    disabled={!canCut || panel.swapped === null || remote.pending?.action === 'swap'}
                    title="Swap sides"
                    aria-label="Swap sides"
                    onClick={() => panel.swap()}
                  >
                    <ArrowLeftRight size={18} aria-hidden="true" />
                  </button>
                  <div className="obs-plate is-right"><small>Right</small><b>{sides[1]}</b></div>
                </div>
                {canCut && panel.swapped === null && (
                  <p className="obs-note mt-2 mb-0">In-Match has no swapped twin in OBS: import the OBS export.</p>
                )}
              </section>
            )}
          </div>
        </div>

        {/* ── activity log ── */}
        <div className="obs-log" aria-live="polite">
          {obs.log.length === 0 ? (
            <span className="text-muted">No activity yet.</span>
          ) : (
            obs.log.map((entry) => (
              <div className={`obs-log__row${entry.ok ? '' : ' is-fail'}`} key={entry.id}>
                <span className="obs-log__time">{logTime(entry.ts)}</span> {entry.text}
              </div>
            ))
          )}
        </div>
      </div>
      <QrScanner
        open={scanning}
        title="Scan OBS's Connect Info"
        hint="In OBS: Tools → WebSocket Server Settings → Show Connect Info. Point the camera at its QR code."
        accept={(text) => !!parseObsws(text)}
        rejectMessage={(text) => `That isn't OBS's QR code (it reads “${text.length > 60 ? `${text.slice(0, 60)}…` : text}”).`}
        onScan={onScanned}
        onClose={() => setScanning(false)}
      />
    </div>
  );
}
